import styles from './pricing-addons.module.css';

// Public display of addon-products.ts cents and credit-config.ts seeded rates.
// Workspace Billing supplies the final quote; no purchase controls belong here.
const prices = {
  AUD: { agent: 40, seat: 25, credit: 0.025 },
  USD: { agent: 25, seat: 16, credit: 0.02 },
} as const;
export function PricingAddons({ currency }: { currency: 'AUD' | 'USD' }) {
  const rates = prices[currency];
  const money = (amount: number) =>
    new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  return (
    <section className={styles.section} aria-label="Optional add-ons">
      <span className={styles.eyebrow}>Room to grow</span>
      <h2>Add capacity when you need it.</h2>
      <p>
        Keep your plan and add more agents, team seats or credits through
        workspace Billing.
      </p>
      <div className={styles.grid}>
        <article>
          <h3>Extra AI agent</h3>
          <strong>
            {money(rates.agent)} <small>{currency} / agent / month</small>
          </strong>
          <p>Add another agent to your workspace.</p>
        </article>
        <article>
          <h3>Extra team seat</h3>
          <strong>
            {money(rates.seat)} <small>{currency} / seat / month</small>
          </strong>
          <p>Bring another team member into your workspace.</p>
        </article>
        <article>
          <h3>One-off credit packs</h3>
          <ul>
            {[5000, 15000, 40000].map((credits) => (
              <li key={credits}>
                <span>{credits.toLocaleString()} credits</span>
                <b>
                  {money(credits * rates.credit)} {currency}
                </b>
              </li>
            ))}
          </ul>
          <p>
            Credits expire at the end of your current billing period and do not
            roll over.
          </p>
        </article>
      </div>
      <div className={styles.usageFees}>
        <article>
          <h3>Recurring extra credits</h3>
          <strong>
            {money(rates.credit * 1000)}{' '}
            <small>{currency} / 1,000 credits / month</small>
          </strong>
          <p>Add ongoing monthly credit capacity alongside your plan.</p>
        </article>
        <article>
          <h3>Optional credit overage</h3>
          <strong>
            {currency === 'AUD' ? '$0.03375' : '$0.027'}{' '}
            <small>{currency} / additional credit</small>
          </strong>
          <p>
            {currency === 'AUD' ? '$33.75 AUD' : '$27 USD'} per 1,000 credits
            used beyond your available balance.
          </p>
          <p>
            Your workspace owner must enable overage. Usage beyond the balance
            is then metered and added to billing at the end of the period. With
            overage off, usage stops when credits run out.
          </p>
        </article>
      </div>

      <p className={styles.note}>
        Agent and seat additions are prorated for the remaining billing period.
        Credit packs are one-off purchases. Your workspace’s billing currency
        and current quote apply.
      </p>
    </section>
  );
}
